import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { TimeoutError, firstValueFrom, timeout } from 'rxjs';
import { I18nService } from '../../../core/i18n';
import { resolveHttpOrRelativeApiUrl } from '../../../shared/utils';
import { normalizeCompanyMembershipRole } from '../../company-context/utils/company-membership-role.util';
import { CompanyMemberListItem } from '../models/company-member-list-item.model';

type JsonRecord = Record<string, unknown>;
type CompanyInviteRole = 'Owner' | 'Admin' | 'Member';

@Injectable({ providedIn: 'root' })
export class CompanyMembersFacade {
  private readonly http = inject(HttpClient);
  private readonly i18n = inject(I18nService);

  private readonly membersSignal = signal<CompanyMemberListItem[]>([]);
  private readonly querySignal = signal('');
  private readonly loadingSignal = signal(false);
  private readonly invitingSignal = signal(false);
  private readonly resendingSignal = signal(false);
  private readonly deactivatingSignal = signal(false);
  private readonly reactivatingSignal = signal(false);
  private readonly removingSignal = signal(false);
  private readonly savingContactSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);

  readonly members = this.membersSignal.asReadonly();
  readonly query = this.querySignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly inviting = this.invitingSignal.asReadonly();
  readonly resending = this.resendingSignal.asReadonly();
  readonly deactivating = this.deactivatingSignal.asReadonly();
  readonly reactivating = this.reactivatingSignal.asReadonly();
  readonly removing = this.removingSignal.asReadonly();
  readonly savingContact = this.savingContactSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  readonly filteredMembers = computed(() => {
    const q = this.querySignal().trim().toLowerCase();
    if (!q) return this.membersSignal();
    const digits = q.replace(/\D/g, '');
    return this.membersSignal().filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.email.toLowerCase().includes(q) ||
        (digits.length > 0 &&
          ((item.whatsAppE164 ?? '').includes(digits) || (item.phoneE164 ?? '').includes(digits)))
    );
  });

  async loadMembers(): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      const response = await firstValueFrom(this.http.get<unknown>('/users'));
      const mapped = this.mapUsersResponse(response);
      this.membersSignal.set(mapped);
    } catch (error) {
      this.membersSignal.set([]);
      if (error instanceof HttpErrorResponse) {
        this.errorSignal.set(this.readApiErrorMessage(error));
      } else {
        this.errorSignal.set(this.i18n.t('settings.members.messages.loadmembersfailed'));
      }
    } finally {
      this.loadingSignal.set(false);
    }
  }

  setQuery(value: string): void {
    this.querySignal.set(value);
  }

  async inviteMember(email: string, role: CompanyInviteRole): Promise<'ok' | 'ack_timeout'> {
    this.invitingSignal.set(true);
    try {
      await firstValueFrom(this.http.post('/users/invite', { email, role }).pipe(timeout(3000)));
      // Nao bloqueia o estado de submit pela recarga da tabela.
      void this.loadMembers();
      return 'ok';
    } catch (error) {
      if (error instanceof TimeoutError) {
        // Alguns ambientes gravam no backend mas demoram para concluir a resposta HTTP.
        // Evita deixar o modal travado em "Salvando...".
        void this.loadMembers();
        return 'ack_timeout';
      }
      if (error instanceof HttpErrorResponse) {
        throw new Error(this.readApiErrorMessage(error));
      }
      throw new Error(this.i18n.t('settings.members.messages.invitefailed'));
    } finally {
      this.invitingSignal.set(false);
    }
  }

  /**
   * Reenvio de convite. O backend (POST /users/invite) reaproveita o convite ativo
   * para o mesmo e-mail e dispara o e-mail novamente — não precisa de endpoint próprio.
   */
  async resendInvite(email: string, role: CompanyInviteRole): Promise<'ok' | 'ack_timeout'> {
    this.resendingSignal.set(true);
    try {
      await firstValueFrom(this.http.post('/users/invite', { email, role }).pipe(timeout(3000)));
      void this.loadMembers();
      return 'ok';
    } catch (error) {
      if (error instanceof TimeoutError) {
        void this.loadMembers();
        return 'ack_timeout';
      }
      if (error instanceof HttpErrorResponse) {
        throw new Error(this.readApiErrorMessage(error));
      }
      throw new Error(this.i18n.t('settings.members.messages.resendfailed'));
    } finally {
      this.resendingSignal.set(false);
    }
  }

  async deactivateMember(userId: string): Promise<void> {
    const id = userId.trim();
    if (!id) return;
    this.deactivatingSignal.set(true);
    try {
      await firstValueFrom(this.http.post(`/users/${encodeURIComponent(id)}/deactivate`, {}));
      void this.loadMembers();
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        throw new Error(this.readApiErrorMessage(error));
      }
      throw new Error(this.i18n.t('settings.members.messages.deactivatefailed'));
    } finally {
      this.deactivatingSignal.set(false);
    }
  }

  async reactivateMember(userId: string): Promise<void> {
    const id = userId.trim();
    if (!id) return;
    this.reactivatingSignal.set(true);
    try {
      const attempts: Array<() => Promise<unknown>> = [
        () => firstValueFrom(this.http.post(`/users/${encodeURIComponent(id)}/activate`, {})),
        () =>
          firstValueFrom(
            this.http.patch(`/users/${encodeURIComponent(id)}`, { isActive: true })
          ),
        () =>
          firstValueFrom(this.http.put(`/users/${encodeURIComponent(id)}`, { isActive: true })),
      ];
      let lastError: unknown;
      for (const run of attempts) {
        try {
          await run();
          void this.loadMembers();
          return;
        } catch (e) {
          lastError = e;
        }
      }
      if (lastError instanceof HttpErrorResponse) {
        throw new Error(this.readApiErrorMessage(lastError));
      }
      throw new Error(this.i18n.t('settings.members.messages.reactivatefailed'));
    } finally {
      this.reactivatingSignal.set(false);
    }
  }

  async removeMemberFromCompany(userId: string): Promise<void> {
    const id = userId.trim();
    if (!id) return;
    this.removingSignal.set(true);
    try {
      await firstValueFrom(this.http.delete(`/users/${encodeURIComponent(id)}`));
      void this.loadMembers();
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        throw new Error(this.readApiErrorMessage(error));
      }
      throw new Error(this.i18n.t('settings.members.messages.removefailed'));
    } finally {
      this.removingSignal.set(false);
    }
  }

  /**
   * Atualiza dados e papel, preservando o estado ativo da linha carregada.
   * A normalização dos telefones para E.164 é feita pela API.
   */
  async updateMemberContact(
    member: CompanyMemberListItem,
    phoneE164: string | null,
    whatsAppE164: string | null,
    details: { name: string; email: string; role: string }
  ): Promise<void> {
    const id = member.id.trim();
    if (!id) return;
    this.savingContactSignal.set(true);
    try {
      await firstValueFrom(
        this.http.put(`/users/${encodeURIComponent(id)}`, {
          name: details.name.trim(),
          email: details.email.trim().toLowerCase(),
          role: normalizeCompanyMembershipRole(details.role, 'Member'),
          isActive: member.isActive,
          phoneE164: phoneE164?.trim() || null,
          whatsAppE164: whatsAppE164?.trim() || null,
        })
      );
      await this.loadMembers();
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 400) {
          throw new Error(this.i18n.t('settings.members.contact.validation'));
        }
        throw new Error(this.readApiErrorMessage(error));
      }
      throw new Error(this.i18n.t('settings.members.messages.contactsavefailed'));
    } finally {
      this.savingContactSignal.set(false);
    }
  }

  private mapUsersResponse(raw: unknown): CompanyMemberListItem[] {
    const root = this.asRecord(raw);
    const source = this.resolveUsersSource(raw, root);
    return source
      .map((item) => this.asRecord(item))
      .map((item) => {
        const nestedUser = this.asRecord(item['user'] ?? item['User']);
        const src = Object.keys(nestedUser).length ? { ...item, ...nestedUser } : item;
        if (this.readIsDeletedFlag(src) || this.readIsDeletedFlag(item)) {
          return null;
        }
        const email = this.asString(src['email']) || '-';
        const entryType = this.readEntryType(src);
        const inviteExpiresAt =
          this.asNullableString(src['inviteExpiresAt']) ?? this.asNullableString(src['invite_expires_at']);
        const inviteAcceptedAt =
          this.asNullableString(src['inviteAcceptedAt']) ?? this.asNullableString(src['invite_accepted_at']);
        const displayName =
          this.asString(src['name']) ||
          this.asString(src['fullName']) ||
          this.extractNameFromEmail(email);
        const avatarRaw =
          this.asNullableString(src['avatarUrl']) ??
          this.asNullableString(src['AvatarUrl']) ??
          this.asNullableString(src['profilePictureUrl']) ??
          this.asNullableString(src['photoUrl']) ??
          this.asNullableString(src['imageUrl']);
        const avatarResolved = avatarRaw?.trim() ? resolveHttpOrRelativeApiUrl(avatarRaw) : '';
        const userId =
          this.asString(
            src['userId'] ??
              src['UserId'] ??
              src['applicationUserId'] ??
              src['ApplicationUserId'] ??
              item['userId'] ??
              item['UserId']
          ).trim() || '';
        const membershipOrRowId =
          this.asString(src['id'] ?? item['id'] ?? item['membershipId'] ?? item['MembershipId']).trim() ||
          '';
        const roleRaw =
          src['role'] ??
          item['role'] ??
          src['Role'] ??
          item['Role'] ??
          src['membershipRole'] ??
          item['membershipRole'] ??
          src['MembershipRole'] ??
          src['companyRole'] ??
          src['CompanyRole'];
        return {
          id: userId || membershipOrRowId || crypto.randomUUID(),
          name: displayName || '-',
          email,
          role: normalizeCompanyMembershipRole(roleRaw, 'Member') as string,
          isActive: entryType === 'invite' ? false : this.asBoolean(src['isActive'], true),
          avatarUrl: avatarResolved || null,
          phoneE164: this.readPhone(src, 'phoneE164', 'PhoneE164'),
          whatsAppE164: this.readPhone(src, 'whatsAppE164', 'WhatsAppE164'),
          entryType,
          inviteExpiresAt,
          inviteAcceptedAt,
        } satisfies CompanyMemberListItem;
      })
      .filter((row): row is CompanyMemberListItem => row != null && row.email !== '-');
  }

  private readPhone(rec: JsonRecord, camelKey: string, pascalKey: string): string | null {
    const raw = this.asNullableString(rec[camelKey]) ?? this.asNullableString(rec[pascalKey]);
    const trimmed = raw?.trim();
    return trimmed ? trimmed : null;
  }

  private readIsDeletedFlag(rec: JsonRecord): boolean {
    const v = rec['isDeleted'] ?? rec['IsDeleted'] ?? rec['is_deleted'];
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') return v.trim().toLowerCase() === 'true';
    if (typeof v === 'number') return v === 1;
    return false;
  }

  private resolveUsersSource(raw: unknown, root: JsonRecord): unknown[] {
    if (Array.isArray(raw)) return raw;
    const items = this.asArray(root['items']);
    if (items.length) return items;

    // Suporta formatos combinados: { members: [], invites: [] } e variações.
    const members = this.asArray(root['members']);
    const users = this.asArray(root['users']);
    const invites = this.asArray(root['invites']);
    const entries = this.asArray(root['entries']);
    return [...members, ...users, ...invites, ...entries];
  }

  private asRecord(value: unknown): JsonRecord {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : {};
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private asNullableString(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
  }

  private asBoolean(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }

  private readEntryType(item: JsonRecord): 'member' | 'invite' {
    const value = (this.asString(item['entryType']) || this.asString(item['entry_type'])).trim().toLowerCase();
    return value === 'invite' ? 'invite' : 'member';
  }

  private extractNameFromEmail(email: string): string {
    if (!email || email === '-') return '';
    const at = email.indexOf('@');
    if (at <= 0) return '';
    return email.slice(0, at);
  }

  private readApiErrorMessage(error: HttpErrorResponse): string {
    const payload = this.asRecord(error.error);
    const known = this.readKnownMembershipError(payload);
    if (known) return known;

    // Prioriza mensagens amigaveis por status para nao exibir texto tecnico/generico da API.
    if (error.status === 0) return this.i18n.t('settings.members.messages.apiconnectionfailed');
    if (error.status === 404) return this.i18n.t('settings.members.messages.endpointnotfound');
    if (error.status === 500) return this.i18n.t('settings.members.messages.internalservererror');
    if (error.status === 503) return this.i18n.t('settings.members.messages.apiunavailable');
    if (error.status === 400) return this.i18n.t('settings.members.messages.invitevalidationfailed');
    if (error.status === 401) return this.i18n.t('settings.members.messages.sessionexpired');
    if (error.status === 403) return this.i18n.t('settings.members.messages.permissiondenied');
    if (error.status === 422) return this.i18n.t('settings.members.messages.deactivatevalidation');
    if (error.status === 409) {
      return this.i18n.t('settings.members.messages.invitealreadyexists');
    }

    const message = this.asString(payload['message']).trim();
    if (message) return message;
    return this.i18n.t('settings.members.messages.invitefailed');
  }

  /** Códigos de negócio devolvidos pela API (ex.: convite). */
  private readKnownMembershipError(payload: JsonRecord): string | null {
    if (this.payloadHasErrorToken(payload, 'User_LastAdmin')) {
      return this.i18n.t('settings.members.messages.lastadmin');
    }
    if (this.payloadHasErrorToken(payload, 'User_EmailInUseGlobally')) {
      return this.i18n.t('settings.members.messages.emailinuseglobally');
    }
    return null;
  }

  private payloadHasErrorToken(payload: JsonRecord, token: string): boolean {
    try {
      return JSON.stringify(payload).includes(token);
    } catch {
      return false;
    }
  }
}
